import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, Plus, Trash2, MoreVertical, Power, PowerOff,
  Clock, Send, Users, FileText, Sparkles, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAgentStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const AgentsPage = () => {
  const { agents, fetchAgents, createAgent, updateAgent, deleteAgent } = useAgentStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    description: '',
    trigger_type: 'auto_assign',
    config: {},
    enabled: true,
  });

  const triggerTypes = [
    { value: 'auto_assign', label: t('auto_assign'), icon: Users, description: language === 'hi' ? 'टास्क स्वचालित रूप से असाइन करें' : 'Automatically assign tasks' },
    { value: 'reminder', label: t('reminder'), icon: Clock, description: language === 'hi' ? 'डेडलाइन के लिए रिमाइंडर भेजें' : 'Send reminders for deadlines' },
    { value: 'report', label: t('report'), icon: FileText, description: language === 'hi' ? 'स्वचालित रिपोर्ट जनरेट करें' : 'Generate automated reports' },
    { value: 'follow_up', label: t('follow_up'), icon: Send, description: language === 'hi' ? 'अधूरे टास्क पर फॉलो अप करें' : 'Follow up on incomplete tasks' },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAgents();
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleCreateAgent = async () => {
    if (!newAgent.name.trim()) {
      toast.error(language === 'hi' ? 'एजेंट का नाम आवश्यक है' : 'Agent name is required');
      return;
    }

    setIsCreating(true);
    try {
      await createAgent(newAgent);
      toast.success(language === 'hi' ? 'एजेंट बनाया गया' : 'Agent created');
      setShowCreateDialog(false);
      setNewAgent({
        name: '',
        description: '',
        trigger_type: 'auto_assign',
        config: {},
        enabled: true,
      });
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleAgent = async (agentId, currentEnabled) => {
    try {
      await updateAgent(agentId, { enabled: !currentEnabled });
      toast.success(
        currentEnabled 
          ? (language === 'hi' ? 'एजेंट निष्क्रिय' : 'Agent disabled')
          : (language === 'hi' ? 'एजेंट सक्रिय' : 'Agent enabled')
      );
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await deleteAgent(agentId);
      toast.success(language === 'hi' ? 'एजेंट हटाया गया' : 'Agent deleted');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const getTypeIcon = (type) => {
    const found = triggerTypes.find(t => t.value === type);
    return found?.icon || Bot;
  };

  const getTypeLabel = (type) => {
    const found = triggerTypes.find(t => t.value === type);
    return found?.label || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            {t('agents')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'hi' ? 'AI एजेंट्स को कॉन्फ़िगर और प्रबंधित करें' : 'Configure and manage AI agents for automation'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="glow-primary"
          data-testid="create-agent-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('new_agent')}
        </Button>
      </div>

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <Card className="glass">
          <CardContent className="py-16 text-center">
            <Bot className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">
              {language === 'hi' ? 'कोई AI एजेंट नहीं' : 'No AI Agents Yet'}
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {language === 'hi' 
                ? 'अपने पहले AI एजेंट बनाएं। एजेंट्स स्वचालित रूप से टास्क असाइन, रिमाइंडर भेज और रिपोर्ट जनरेट कर सकते हैं।'
                : 'Create your first AI agent to automate tasks, send reminders, generate reports, and more.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent, index) => {
            const TypeIcon = getTypeIcon(agent.trigger_type);
            
            return (
              <motion.div
                key={agent.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className={`glass card-hover ${!agent.enabled ? 'opacity-60' : ''}`}
                  data-testid={`agent-card-${agent.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                        agent.enabled ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        <TypeIcon className={`h-6 w-6 ${agent.enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleToggleAgent(agent.id, agent.enabled)}
                          >
                            {agent.enabled ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-2" />
                                {t('disabled')}
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-2" />
                                {t('enabled')}
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteAgent(agent.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="mt-4">
                      <h3 className="font-heading font-semibold">{agent.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {agent.description}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                        {getTypeLabel(agent.trigger_type)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${agent.enabled ? 'bg-chart-3' : 'bg-muted-foreground'}`} />
                        <span className="text-xs text-muted-foreground">
                          {agent.enabled ? t('enabled') : t('disabled')}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('new_agent')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('agent_name')}</Label>
              <Input
                value={newAgent.name}
                onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                placeholder={language === 'hi' ? 'एजेंट का नाम' : 'Agent name'}
                className="bg-secondary/50 border-white/10"
                data-testid="agent-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('agent_description')}</Label>
              <Textarea
                value={newAgent.description}
                onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                placeholder={language === 'hi' ? 'यह एजेंट क्या करेगा?' : 'What will this agent do?'}
                className="bg-secondary/50 border-white/10"
                data-testid="agent-desc-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('trigger_type')}</Label>
              <Select
                value={newAgent.trigger_type}
                onValueChange={(value) => setNewAgent({ ...newAgent, trigger_type: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-white/10" data-testid="trigger-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {triggerTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {triggerTypes.find(t => t.value === newAgent.trigger_type)?.description}
              </p>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>{language === 'hi' ? 'सक्रिय' : 'Enabled'}</Label>
                <p className="text-xs text-muted-foreground">
                  {language === 'hi' ? 'एजेंट को तुरंत सक्रिय करें' : 'Enable agent immediately'}
                </p>
              </div>
              <Switch
                checked={newAgent.enabled}
                onCheckedChange={(checked) => setNewAgent({ ...newAgent, enabled: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleCreateAgent}
              disabled={isCreating}
              className="glow-primary"
              data-testid="submit-agent-btn"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              {language === 'hi' ? 'एजेंट बनाएं' : 'Create Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentsPage;
